import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CountryWar from "@/components/CountryWar";
import ModeCountdowns from "@/components/ModeCountdowns";
import WelcomeTour from "@/components/WelcomeTour";
import ChampionshipCountdown from "@/components/ChampionshipCountdown";
import ChampionshipInvitePopup from "@/components/ChampionshipInvitePopup";
import SeasonWelcomeBanner from "@/components/SeasonWelcomeBanner";
import MeetQualifiers from "@/components/MeetQualifiers";
import PlayerOverview from "@/components/PlayerOverview";
import SubtlePremiumStrip from "@/components/SubtlePremiumStrip";
import { CountryFlag, COUNTRY_NAME } from "@/lib/identity";
import {
  ArrowRight, Broadcast, Stairs, Sword, Fire, Star, Crown, Play,
} from "@phosphor-icons/react";

const MISSION_ICONS = {
  lightning: Broadcast,
  trophy: Stairs,
  skull: Fire,
  "calendar-star": Star,
  sparkle: Crown,
  crosshair: Sword,
  chat: Broadcast,
};

export default function Home() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [mission, setMission] = useState(null);

  useEffect(() => {
    api.get("/season/profile/me").then((r) => setProfile(r.data)).catch(() => {});
    api.get("/modes/mission").then((r) => setMission(r.data)).catch(() => {});
  }, []);

  const isNew = (profile?.stats?.games_played ?? 0) < 3;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="home-root">
      {/* 1 · Greeting (smaller, less saturated) */}
      <section className="animate-fade-up">
        <p className="eyebrow mb-1.5 text-white/55">{greeting()}</p>
        <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight text-white">
          {user?.username ? (
            <>Welcome back, <span className="italic font-light text-nk-gold">{user.username}</span></>
          ) : "Welcome back"}
        </h1>
        {user?.country && (
          <p className="text-xs sm:text-sm text-white/60 mt-1.5 flex items-center flex-wrap gap-1.5">
            Playing for <CountryFlag country={user.country} size={12} /> {COUNTRY_NAME[user.country] || ""}.
          </p>
        )}
      </section>

      {/* 2 · Welcome tour — only for brand-new players */}
      {isNew && (
        <div className="animate-fade-up" style={{ animationDelay: "60ms" }}>
          <WelcomeTour username={user?.username} />
        </div>
      )}

      {/* 2.5 · New-season welcome banner (top-5 recap from previous season) */}
      <div className="animate-fade-up" style={{ animationDelay: "80ms" }}>
        <SeasonWelcomeBanner />
      </div>

      {/* 2.7 · Championship countdown (shows from Day 25 onwards) */}
      <div className="animate-fade-up" style={{ animationDelay: "100ms" }}>
        <ChampionshipCountdown />
      </div>

      {/* Championship invite popup — floats bottom-right for pending invitees */}
      <ChampionshipInvitePopup />

      {/* 2.8 · Meet Qualifiers (shows from Day 28 onwards) */}
      <div className="animate-fade-up" style={{ animationDelay: "110ms" }}>
        <MeetQualifiers />
      </div>

      {/* 3 · HERO — "Right Now" mission */}
      <div className="animate-fade-up" style={{ animationDelay: "120ms" }}>
        <HeroRightNow mission={mission} />
      </div>

      {/* 4 · Stat ribbon (compact) */}
      <div className="animate-fade-up" style={{ animationDelay: "180ms" }}>
        <StatRibbon profile={profile} user={user} />
      </div>

      {/* 5 · Overview — game-mode stats, championship qualification, titles.
             Moved here from the Profile "Overview" tab. */}
      <div className="animate-fade-up" style={{ animationDelay: "220ms" }}>
        <PlayerOverview />
      </div>

      {/* 6 · Mode countdowns (LIVE modes: Daily / Saturday / Championship) */}
      <div className="animate-fade-up" style={{ animationDelay: "280ms" }}>
        <ModeCountdowns />
      </div>

      {/* 7 · NK+ subtle strip — replaces the loud premium/house-ad banner */}
      <div className="animate-fade-up" style={{ animationDelay: "340ms" }}>
        <SubtlePremiumStrip />
      </div>

      {/* 8 · Country War (single tile, no season-countdown sibling) */}
      <section className="animate-fade-up" style={{ animationDelay: "400ms" }}>
        <CountryWar />
      </section>

      {/* Mobile sticky "Play Now" FAB */}
      <Link
        to="/daily"
        data-testid="home-play-fab"
        aria-label="Play NK Daily now"
        className="lg:hidden fixed right-4 bottom-20 z-30 w-14 h-14 rounded-full bg-nk-gold text-nk-charcoal flex items-center justify-center shadow-xl border-2 border-nk-gold/60 active:scale-95 transition-transform nk-hero-glow"
      >
        <Play size={22} weight="fill" />
      </Link>
    </div>
  );
}

/* ===================== Hero ===================== */
function HeroRightNow({ mission }) {
  const Icon = MISSION_ICONS[mission?.icon] || Broadcast;
  const isLive = mission?.urgency === "live";
  const isGold = mission?.tone === "gold";

  return (
    <Link
      to={mission?.cta_to || "/daily"}
      data-testid="mission-card-cta"
      className={`relative block overflow-hidden rounded-md p-5 sm:p-7 transition-all active:scale-[0.99] ${isLive ? "nk-hero-glow" : ""}`}
      style={{
        background: isGold
          ? "linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(11,61,46,0.55) 60%, rgba(10,15,18,0.85) 100%)"
          : "linear-gradient(135deg, rgba(11,61,46,0.75) 0%, rgba(16,21,24,0.85) 80%)",
        border: `1px solid ${isGold ? "rgba(212,175,55,0.55)" : "rgba(212,175,55,0.25)"}`,
      }}
    >
      <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-nk-gold/8 blur-3xl pointer-events-none" />

      <div className="relative flex items-start gap-4">
        <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-md bg-black/30 border border-nk-gold/30 flex items-center justify-center">
          <Icon size={26} weight={isGold ? "fill" : "duotone"} className="text-nk-gold" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-nk-gold/85 flex items-center gap-1.5">
            {isLive ? (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-nk-bad animate-pulse-dot" />
                Live now
              </>
            ) : "Right now"}
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-medium tracking-tight leading-tight text-white mt-1.5">
            {mission?.label || "Find your next game"}
          </h2>
          <div className="mt-4 inline-flex items-center gap-2 nk-btn nk-btn-gold">
            {isLive ? "Join now" : "Take me there"}
            <ArrowRight size={14} weight="bold" />
          </div>
        </div>
      </div>
    </Link>
  );
}

/* ===================== Stat ribbon ===================== */
function StatRibbon({ profile, user }) {
  const kr = profile?.stats?.knowledge_rating ?? null;
  const season = profile?.stats?.season_score ?? 0;
  const rank = profile?.season_rank ? `#${profile.season_rank}` : "Unranked";
  const empty = (profile?.stats?.games_played ?? 0) === 0;

  return (
    <div className="nk-card p-4 sm:p-5" data-testid="your-numbers-header">
      <div className="flex items-baseline justify-between mb-3">
        <p className="eyebrow text-white/65">Your numbers</p>
        <Link to="/history" data-testid="home-history-link"
              className="text-xs text-nk-gold inline-flex items-center gap-1 hover:gap-2 transition-all">
          Match history <ArrowRight size={11} />
        </Link>
      </div>

      {empty ? (
        <div className="text-center py-4">
          <p className="text-sm text-white/70">No games yet. Play your first round to fill this in.</p>
          <Link to="/daily" data-testid="empty-stats-cta" className="nk-btn nk-btn-primary mt-3 inline-flex">
            Play NK Daily <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:gap-5">
          <Stat label="Rating" value={kr ?? "—"} />
          <Stat label="Season" value={season.toLocaleString()} highlight />
          <Stat label={user?.country || "Rank"} value={rank} />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div className="border-l border-white/8 pl-3 first:border-l-0 first:pl-0">
      <p className="text-[10px] uppercase tracking-[0.16em] text-white/40 font-medium">{label}</p>
      <p className={`font-display text-2xl sm:text-3xl font-medium mt-1 tabular-nums ${highlight ? "text-nk-gold" : "text-white"}`}>
        {value}
      </p>
    </div>
  );
}

/* ===================== Greeting helper ===================== */
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}
